import { useEffect, useState, useSyncExternalStore } from 'react'
import type { FanAiConfig } from './types'
import { MEMBER_TOKEN_KEY, onMemberSessionEnded } from '../../services/memberApi'
import { credentialKey, ensureProbe, getVerdict, isPrivateSite, publicAuthHeaders, publicCredential, subscribeVerdicts } from '../../services/publicAccess'
import { api } from '../../services/api'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

/** `?sport=` suffix for the Fan AI endpoints (empty when no sport is known). */
export function fanAiSportQuery(sportId: string | null | undefined): string {
  return sportId ? `?sport=${encodeURIComponent(sportId)}` : ''
}

// The bubble is mounted in AppRoutes, outside SiteTemplateHost (and so
// outside MemberAuthProvider and SiteAccessProvider): it learns about a
// private site's credential from the verdict store (a probe answered, or
// SiteAccessProvider announced an in-tab sign-in / sign-out), another tab's
// storage change, and a member session ended by a 401.
function subscribeAccess(fn: () => void): () => void {
  const offVerdicts = subscribeVerdicts(fn)
  const offEnded = onMemberSessionEnded(fn)
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === MEMBER_TOKEN_KEY || e.key === 'mcl_admin_token') fn()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    offVerdicts()
    offEnded()
    window.removeEventListener('storage', onStorage)
  }
}

/**
 * '' on a public league (no storage read); on a members-only league the
 * admitted credential's key, else WALLED (no credential) or `walled:<key>`
 * (a credential not admitted yet): the assistant is only offered to a
 * visitor the gate admitted, so it never sits over the sign-in wall. The
 * not-admitted snapshot carries the key so a credential change while still
 * walled (a sign-in in another tab, or a session_ended member token falling
 * back to a staff token) re-runs the effect, which probes the new credential.
 * Credential keys are `member:…` / `staff:…`, so they never start with WALLED.
 */
const WALLED = 'walled'
function accessSnapshot(): string {
  if (!isPrivateSite()) return ''
  const key = credentialKey(publicCredential())
  if (!key) return WALLED
  return getVerdict(key)?.kind === 'admitted' ? key : `${WALLED}:${key}`
}
const isWalled = (snap: string): boolean => snap === WALLED || snap.startsWith(`${WALLED}:`)

// Loads the public assistant config for the current tenant and sport.
// Returns `null` whenever the assistant is unavailable: switched off in
// settings, the league isn't entitled to Fan AI, its subscription lapsed or
// the league is disabled (the API answers 404 { error: 'disabled' } for all
// of these, #328), so the caller simply doesn't render the bubble. Errors are
// silent too — a flaky API shouldn't break the rest of the page.
//
// `sportId` is the fan's active sport: the backend keeps one Fan AI config
// per sport, so without it a multi-sport tenant always got the default
// sport's on/off switch.
export function useFanAiConfig(sportId?: string | null): { config: FanAiConfig | null; loading: boolean } {
  const [config, setConfig] = useState<FanAiConfig | null>(null)
  const [loading, setLoading] = useState(true)
  // A members-only league (registration step 33) gates both Fan AI routes:
  // the config read carries the member (else staff) Bearer, and re-runs when
  // the admitted credential changes, so the bubble appears after a sign-in
  // and disappears on a sign-out without a navigation.
  const cred = useSyncExternalStore(subscribeAccess, accessSnapshot)

  useEffect(() => {
    let cancelled = false
    // Not admitted on a private league: the read could only be refused.
    if (isWalled(cred)) {
      // The standalone ticket routes (/tickets/success, a wallet link) sit
      // outside SiteTemplateHost, so SiteAccessProvider never probes there:
      // probe here when a credential exists and no verdict is stored yet
      // (idempotent per credential, never doubled; a stored refused or
      // unavailable verdict stops any loop). The snapshot carries the
      // credential key, so a new credential re-runs this effect and is
      // probed too, and an admitted verdict re-runs it through the store.
      const sent = publicCredential()
      if (isPrivateSite() && sent) ensureProbe(sent, () => api.getSiteAccess())
      setConfig(null)
      setLoading(false)
      return
    }
    setLoading(true)
    const url = `${API_BASE_URL}/public/fan-ai/config${fanAiSportQuery(sportId)}`
    ;(cred ? fetch(url, { headers: publicAuthHeaders() }) : fetch(url))
      .then(async (res) => {
        if (!res.ok) return null
        const body = (await res.json()) as FanAiConfig
        return body && body.enabled ? body : null
      })
      .then((cfg) => {
        if (cancelled) return
        setConfig(cfg)
      })
      .catch(() => {
        if (cancelled) return
        setConfig(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [sportId, cred])

  return { config, loading }
}
